// 바인딩 {label} placeholder 전용 — 좁은 셀/텍스트박스에서 줄바꿈이 생기지 않도록 폰트 크기를
// 부모 가용폭에 맞춰 동적으로 축소하고, 축소 한계(min)에서도 넘치면 말줄임(…)으로 자른다.
// 전체 라벨은 hover 툴팁(title)으로 항상 확인 가능.
//
// placeholder는 admin 편집/미리보기(bindingPlaceholders on — true/'unresolvable-only')에서만 렌더되므로 web/PDF 실출력엔
// 영향이 없다(실값은 이 컴포넌트를 거치지 않음). white-space:nowrap로 한 줄을 강제한 뒤, 부모
// (td/텍스트 div) content 폭보다 넘치면 em 배율(=avail/natural)로 폰트를 줄인다(min까지). 선형
// 스케일이라 1패스로 맞고, 부모 크기 변화(칼럼 리사이즈/줌 재레이아웃)는 ResizeObserver로 추종한다.
//
// fill(표 셀 전용) 모드는 span을 width:0 + min-width:100%로 렌더한다 — hug 표(전 칼럼 구체값
// colWidths, width:auto)는 CSS 스펙상 table-layout:fixed가 auto 알고리즘으로 degrade되는데, nowrap
// placeholder의 자연 폭이 칼럼 intrinsic 폭에 기여하면 JS 측정 전에 칼럼이 라벨 폭만큼 먼저 넓어져
// 축소가 영영 미발동한다(부모가 이미 넓어 natural ≤ avail). width:0은 intrinsic 기여를 0으로 만들어
// 칼럼이 colWidths대로 유지되게 하고, 최종 레이아웃에선 min-width:100%(%는 intrinsic 계산에서 0
// 취급)가 셀 폭으로 편다.
// ⚠ fill을 텍스트 노드에 쓰면 안 됨 — hug 텍스트 박스는 intrinsic이 0이 되어 박스가 0폭으로
// 붕괴하고 형제와 겹친다(2026-07-04 실사고: row의 범례 텍스트 2개가 같은 위치에 이중 인쇄처럼 포개짐).
// 비-fill(텍스트 노드)은 maxWidth:100%로 intrinsic을 보존한다.
//
// 비-fill의 남은 구멍: hug 텍스트 박스는 부모 폭이 placeholder 자연 폭을 따라가므로(원인이자 결과)
// 폭 기반 축소가 영영 미발동 — 긴 카탈로그 라벨이 실값(예: ① 한 글자) 자리를 수백px로 부풀린다.
// 처방 = 글자수 기반 축약: 라벨이 길면 표시만 `{위험성평가 설정(매트…}`로 줄이고(중괄호 보존)
// 전체 라벨은 title 툴팁으로. intrinsic이 축약 폭으로 제한돼 hug 박스가 실값 크기에 근접한다.
// fill(셀)은 폭 기반 ellipsis가 더 정밀하므로 글자수 축약 미적용.

import { useLayoutEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

export function FitPlaceholder({
  text,
  min = 0.6,
  fill = false,
  title,
}: {
  text: string;
  /** 폰트 축소 하한(em 배율). 한계 도달 후 잔여 넘침은 ellipsis로 처리. */
  min?: number;
  /** 표 셀 전용 — 칼럼 intrinsic 폭 기여 0(width:0+min-width:100%). 텍스트 노드엔 금지(박스 붕괴). */
  fill?: boolean;
  /** 툴팁 override('unresolvable-only' 모드의 안내 문구 등). 미지정=전체 라벨(text, 회귀 0). */
  title?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);

  // 비-fill(텍스트 노드) 글자수 축약 — `{라벨}` 꼴이면 내부만 잘라 닫는 중괄호를 보존한다.
  const INNER_MAX = 12;
  let display = text;
  if (!fill && text.length > INNER_MAX + 3) {
    const braced = text.startsWith('{') && text.endsWith('}');
    const inner = braced ? text.slice(1, -1) : text;
    if (inner.length > INNER_MAX) {
      const cut = `${inner.slice(0, INNER_MAX)}…`;
      display = braced ? `{${cut}}` : cut;
    }
  }

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const fit = () => {
      // 배율 1(자연 폭)로 되돌려 측정 → 넘치면 가용폭 비율로 축소(min 하한).
      el.style.fontSize = '1em';
      const cs = getComputedStyle(parent);
      const avail =
        parent.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      const natural = el.scrollWidth;
      if (natural > avail && avail > 0) {
        el.style.fontSize = `${Math.max(min, avail / natural)}em`;
      }
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [text, min, fill]);

  // display:inline-block + nowrap → scrollWidth가 전체 텍스트 폭(줄바꿈 없이).
  // overflow:hidden + ellipsis → min 배율에서도 넘치는 잔여는 …로 잘라 이웃 셀/노드 침범 방지.
  const style: CSSProperties = {
    whiteSpace: 'nowrap',
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'bottom',
    ...(fill ? { width: 0, minWidth: '100%' } : { maxWidth: '100%' }),
  };
  return (
    <span ref={ref} style={style} title={title ?? text}>
      {display}
    </span>
  );
}
